import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto, VehicleRecallDto } from 'dtos'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import { VehicleToDoListParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'
import { AddVINDialog, ExplanationAccordion, SortableTableCell } from 'components'
import { enqueueSnackbar } from 'notistack'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { Add, Edit } from '@mui/icons-material'
import moment from 'moment'
import { ListBody } from 'components/list'

export default function NeedsCaRoUploadedToDoListDetails() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [addVINDialogOpen, setAddVINDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [loadingState, setLoadingState] = useLoadingState({ isGettingVehicleRecalls: false, isCreatingVehicle: false })
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'recallCreatedDate',
    order: 'asc'
  })
  const [selectedVehicleRecalls, setSelectedVehicleRecalls] = useState<VehicleRecallDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicleRecalls, setVehicleRecalls] = useState<VehicleRecallDto[]>()

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  const getNeedsCaRoUploadedToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingVehicleRecalls', true)
    controllers.toDoLists
      .getNeedsCaRoUploadedToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicleRecalls(response.value)
        console.log(response)
      })
      .finally(() => {
        setLoadingState('isGettingVehicleRecalls', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Needs CA RO Uploaded')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(loadingState.isGettingVehicleRecalls)
  }, [layout, loadingState.isGettingVehicleRecalls])

  useEffect(() => {
    getNeedsCaRoUploadedToDoList(parameters)
  }, [getNeedsCaRoUploadedToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={loadingState.isCreatingVehicle}
        onClose={() => {
          setAddVINDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isCreatingVehicle', true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setAddVINDialogOpen(false)
              getNeedsCaRoUploadedToDoList(parameters)
            })
            .finally(() => {
              setLoadingState('isCreatingVehicle', false)
            })
        }}
        open={addVINDialogOpen}
      />
      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto' container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='recallCreatedDate'>Recall Created Date</MenuItem>
                    <MenuItem value='exporter'>Exporter</MenuItem>
                    <MenuItem value='buyer'>Buyer</MenuItem>
                    <MenuItem value='vehicleType'>Vehicle Type</MenuItem>
                    <MenuItem value='vin'>VIN</MenuItem>
                    <MenuItem value='year'>Year</MenuItem>
                    <MenuItem value='make'>Make</MenuItem>
                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'exporter', 'buyer', 'vehicleType', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {filterField === 'recallCreatedDate' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField || (filterField === 'recallCreatedDate' ? !filterValueFrom || !filterValueTo : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'recallCreatedDate') {
                        setParameters({
                          ...parameters,
                          recallCreatedDateFrom: filterValueFrom,
                          recallCreatedDateTo: filterValueTo
                        })
                      } else {
                        setParameters({
                          ...parameters,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        parameters.recallCreatedDateFrom ||
                          parameters.recallCreatedDateTo ||
                          parameters.exporter ||
                          parameters.vehicleType ||
                          parameters.vin ||
                          parameters.year ||
                          parameters.make ||
                          parameters.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setParameters({
                        ...parameters,
                        recallCreatedDateFrom: undefined,
                        recallCreatedDateTo: undefined,
                        exporter: undefined,
                        buyer: undefined,
                        vehicleType: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>
              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setAddVINDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {parameters.recallCreatedDateFrom && parameters.recallCreatedDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Recall Created Date from "${moment(parameters.recallCreatedDateFrom).format(
                      'M/D/yyyy'
                    )}" to "${moment(parameters.recallCreatedDateTo).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        recallCreatedDateFrom: undefined,
                        recallCreatedDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
              {parameters.exporter && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter contains "${parameters.exporter}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        exporter: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
              {parameters.buyer && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Buyer contains "${parameters.buyer}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        buyer: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vehicleType && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Type contains "${parameters.vehicleType}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vehicleType: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains ${parameters.vin}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vin: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${parameters.year}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        year: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains ${parameters.make}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        make: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains ${parameters.model}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        model: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding='checkbox'>
                              <Checkbox
                                color='primary'
                                checked={selectedVehicleRecalls.length === totalCount}
                                indeterminate={
                                  selectedVehicleRecalls.length > 0 && selectedVehicleRecalls.length !== totalCount
                                }
                                onChange={() => {
                                  if (selectedVehicleRecalls.length === totalCount) {
                                    setSelectedVehicleRecalls([])
                                  } else {
                                    setSelectedVehicleRecalls(vehicleRecalls ?? [])
                                  }
                                }}
                              />
                            </TableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='recallCreatedDate'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Recall Created Date
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='exporter'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Exporter
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='buyer'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Buyer
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vehicleType'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Vehicle Type
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vin'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              VIN
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='year'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Year
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='make'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Make
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='model'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Model
                            </SortableTableCell>
                            <TableCell padding='checkbox'></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {vehicleRecalls?.map((vehicleRecall, index) => (
                            <TableRow
                              key={vehicleRecall.vehicle?.vin}
                              sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}
                            >
                              <TableCell padding='checkbox' sx={{ fontSize: '7.99pt' }}>
                                <Checkbox
                                  color='primary'
                                  checked={selectedVehicleRecalls.some(
                                    recall => recall.vehicle?.vehicleGuid === vehicleRecall.vehicle?.vehicleGuid
                                  )}
                                  onChange={() => {
                                    if (
                                      selectedVehicleRecalls.some(
                                        recall => recall.vehicle?.vehicleGuid === vehicleRecall.vehicle?.vehicleGuid
                                      )
                                    ) {
                                      setSelectedVehicleRecalls(
                                        selectedVehicleRecalls.filter(
                                          recall => recall.vehicle?.vehicleGuid !== vehicleRecall.vehicle?.vehicleGuid
                                        )
                                      )
                                    } else {
                                      setSelectedVehicleRecalls([...selectedVehicleRecalls, vehicleRecall])
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={theme => ({
                                  color:
                                    moment().diff(moment(formatDate(vehicleRecall.createdDateTime)), 'days') >= 4
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    moment().diff(moment(formatDate(vehicleRecall.createdDateTime)), 'days') >= 1
                                      ? 'bold'
                                      : 'inherit'
                                })}
                              >
                                {formatDate(vehicleRecall?.createdDateTime)}
                              </TableCell>

                              <TableCell>{vehicleRecall.vehicle?.exporterCompany?.name}</TableCell>

                              <TableCell>{vehicleRecall.vehicle?.buyerCompany?.name}</TableCell>

                              <TableCell>{vehicleRecall.vehicle?.vehicleType?.name}</TableCell>

                              <TableCell>{vehicleRecall.vehicle?.vin}</TableCell>

                              <TableCell>{vehicleRecall.vehicle?.year}</TableCell>

                              <TableCell>{vehicleRecall.vehicle?.make}</TableCell>

                              <TableCell>{vehicleRecall.vehicle?.model}</TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => {
                                    navigate('/vehicles/' + vehicleRecall.vehicle?.vehicleGuid + '/recall')
                                  }}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={parameters.pageSize}
                              page={parameters.page}
                              onPageChange={(_, page) => {
                                setParameters({
                                  ...parameters,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setParameters({
                                  ...parameters,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                Vehicles Appear in this to do list if the vehicle has at least 1 recall that has Recall Status = Open, Repair
                Country = Canada, and Repair Order (RO) has not been uploaded. Vehicles may appear multiple times in this To Do
                List (once per recall with matching criteria). || The Needs CA RO Uploaded "To Do List" column "Recall Date"
                value will be in bold and red if the current date is greater than Recall Date + 1 day. || Pressing "Edit" in the
                row of a vehicle navigates to that vehicle's "Vehicle Details - Recall" tab. || The Needs CA RO Uploaded "To Do
                List" is a list screen intended for users on a PC.
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
